import {
    useEffect,
    useState,
    type ReactNode
} from "react";

import {
    Link
} from "react-router";

import {
    getPlacementResult
} from "../core/placementEngine.js";

import {
    NewsCard
} from "../features/news/NewsCatalog.js";

import {
    DailySessionCallout
} from "../features/practice/DailySessionCallout.js";

import {
    useContent
} from "../services/content/ContentProvider.js";

import {
    loadPublishedHomeFeatures,
    loadPublishedNewsIndex
} from "../services/content/publicContentReaders.js";

import {
    useI18n
} from "../i18n/I18nProvider.js";

import type {
    HomeFeature,
    Level,
    NewsIndexItem
} from "../types/global.js";

import {
    Page
} from "../ui/components/Layout.js";

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Application home page.
 *
 * Editorial Home cards are exclusively loaded from published `home_feature`
 * revisions.
 *
 * There is intentionally no static/i18n fallback for these cards.
 */
function HomePage() {
    const {
        repository,
        status:
            contentStatus
    } =
        useContent();

    const {
        localizedTextClass,
        localizedValue,
        t
    } =
        useI18n();


    /* ---------------------------------------------------------------------- */
    /* Learner                                                               */
    /* ---------------------------------------------------------------------- */

    const level:
        Level =
        getPlacementResult()
        ?? "A1";


    /* ---------------------------------------------------------------------- */
    /* Current Journal article                                               */
    /* ---------------------------------------------------------------------- */

    const [
        currentNews,
        setCurrentNews
    ] =
        useState<
            NewsIndexItem
            | null
        >(
            null
        );


    /* ---------------------------------------------------------------------- */
    /* Dynamic Home editorial content                                        */
    /* ---------------------------------------------------------------------- */

    const [
        homeFeatures,
        setHomeFeatures
    ] =
        useState<
            HomeFeature[]
        >(
            []
        );


    /* ---------------------------------------------------------------------- */
    /* Load published content                                                */
    /* ---------------------------------------------------------------------- */

    useEffect(
        () => {
            if (
                contentStatus
                    !== "ready"
                || !repository
            ) {
                return;
            }

            const contentRepository =
                repository;

            let active =
                true;


            /*
             * Reset the server-owned Home section immediately.
             *
             * This prevents old cards from remaining visible if the content
             * repository changes during the session.
             */
            setHomeFeatures(
                []
            );


            /* ------------------------------------------------------------------ */
            /* Home editorial cards                                               */
            /* ------------------------------------------------------------------ */

            async function loadHomeFeatures():
            Promise<void> {
                try {
                    const features =
                        await loadPublishedHomeFeatures(
                            contentRepository
                        );

                    if (
                        !active
                    ) {
                        return;
                    }

                    setHomeFeatures(
                        features
                    );
                } catch (error) {
                    if (
                        !active
                    ) {
                        return;
                    }

                    /*
                     * No fallback:
                     *
                     * a failed Home editorial request does not resurrect
                     * historical hardcoded cards.
                     */
                    setHomeFeatures(
                        []
                    );

                    console.warn(
                        "Home editorial content unavailable:",
                        error
                    );
                }
            }


            /* ------------------------------------------------------------------ */
            /* Current Journal card                                               */
            /* ------------------------------------------------------------------ */

            async function loadCurrentNews():
            Promise<void> {
                try {
                    const news =
                        (
                            await loadPublishedNewsIndex(
                                contentRepository
                            )
                        )[0]
                        ?? null;

                    if (
                        !active
                    ) {
                        return;
                    }

                    setCurrentNews(
                        news
                    );
                } catch (error) {
                    if (
                        !active
                    ) {
                        return;
                    }

                    /*
                     * The Journal highlight is independent from the configurable
                     * editorial Home section.
                     */
                    setCurrentNews(
                        null
                    );

                    console.warn(
                        "Home news skipped:",
                        error
                    );
                }
            }


            void loadHomeFeatures();
            void loadCurrentNews();


            return () => {
                active =
                    false;
            };
        },
        [
            repository,
            contentStatus
        ]
    );


    /* ---------------------------------------------------------------------- */
    /* Render                                                                */
    /* ---------------------------------------------------------------------- */

    return (
        <Page>
            {/* -------------------------------------------------------------- */}
            {/* Learner header                                                 */}
            {/* -------------------------------------------------------------- */}

            <header
                className="
                    mb-7
                    sm:mb-9
                "
            >
                <div
                    className="
                        flex
                        items-start
                        gap-3
                        sm:items-center
                        sm:gap-4
                    "
                >
                    <h1
                        className="
                            text-2xl
                            font-bold
                            leading-tight
                            text-ink
                            sm:text-3xl
                        "
                    >
                        {t(
                            "home.greeting"
                        )}
                    </h1>
                </div>

                <p
                    className="
                        mt-3
                        text-base
                        text-muted
                    "
                >
                    <strong
                        className="
                            font-bold
                            text-dino-700
                        "
                    >
                        {level}
                    </strong>

                    {" · "}

                    {t(
                        "home.currentLevel"
                    )}
                </p>
            </header>


            {/* -------------------------------------------------------------- */}
            {/* Daily learning session                                         */}
            {/* -------------------------------------------------------------- */}

            <DailySessionCallout />


            {/* -------------------------------------------------------------- */}
            {/* Current Journal article                                        */}
            {/* -------------------------------------------------------------- */}

            {currentNews ? (
                <section
                    className="
                        mb-10
                    "
                >
                    <NewsCard
                        article={
                            currentNews
                        }
                    />
                </section>
            ) : null}


            {/* -------------------------------------------------------------- */}
            {/* Dynamic editorial section                                      */}
            {/* -------------------------------------------------------------- */}

            {homeFeatures.length > 0 ? (
                <section>
                    <div
                        className="
                            mb-5
                            flex
                            items-center
                            justify-between
                            gap-4
                        "
                    >
                        <h2
                            className="
                                text-lg
                                font-bold
                                text-ink
                                sm:text-xl
                            "
                        >
                            {t(
                                "home.newsAdvice"
                            )}
                        </h2>

                        <Link
                            to="/journal"
                            className="
                                inline-flex
                                min-h-11
                                items-center
                                text-sm
                                font-bold
                                text-dino-700
                                no-underline
                                hover:underline
                                hover:underline-offset-4
                            "
                        >
                            {t(
                                "news.journalTitle"
                            )}

                            {" →"}
                        </Link>
                    </div>

                    <div
                        className="
                            grid
                            gap-4
                            md:grid-cols-2
                        "
                    >
                        {homeFeatures.map(
                            feature => {
                                const label =
                                    localizedValue(
                                        feature.label,
                                        feature.label_fa,
                                        feature.label
                                    );

                                const title =
                                    localizedValue(
                                        feature.title,
                                        feature.title_fa,
                                        feature.title
                                    );

                                const meta =
                                    localizedValue(
                                        feature.meta,
                                        feature.meta_fa
                                    );

                                return (
                                    <HomeEditorialCard
                                        key={
                                            feature.id
                                        }
                                        feature={
                                            feature
                                        }
                                        label={
                                            label
                                        }
                                        title={
                                            title
                                        }
                                        meta={
                                            meta
                                        }
                                        textClassName={
                                            localizedTextClass()
                                        }
                                    />
                                );
                            }
                        )}
                    </div>
                </section>
            ) : null}
        </Page>
    );
}


/* -------------------------------------------------------------------------- */
/* Dynamic editorial card                                                     */
/* -------------------------------------------------------------------------- */

interface HomeEditorialCardProps {
    feature:
        HomeFeature;

    label:
        string;

    title:
        string;

    meta:
        string;

    textClassName:
        string;
}


function HomeEditorialCard({
    feature,
    label,
    title,
    meta,
    textClassName
}: HomeEditorialCardProps) {
    const content = (
        <article>
            {/* -------------------------------------------------------------- */}
            {/* Visual                                                         */}
            {/* -------------------------------------------------------------- */}

            <div
                className={`
                    flex
                    items-center
                    justify-center
                    bg-dino-50
                    ${
                        feature.featured
                            ? "h-36 sm:h-44"
                            : "h-24 sm:h-28"
                    }
                `}
            >
                <span
                    className={`
                        leading-none
                        transition
                        group-hover:scale-105
                        ${
                            feature.featured
                                ? "text-6xl"
                                : "text-5xl"
                        }
                    `}
                    aria-hidden="true"
                >
                    {feature.icon}
                </span>
            </div>


            {/* -------------------------------------------------------------- */}
            {/* Copy                                                           */}
            {/* -------------------------------------------------------------- */}

            <div
                className={`
                    p-4
                    sm:p-5
                    ${textClassName}
                `}
            >
                <p
                    className="
                        text-xs
                        font-bold
                        uppercase
                        tracking-wider
                        text-dino-700
                    "
                >
                    {label}
                </p>

                <h3
                    className={`
                        mt-2
                        font-semibold
                        leading-snug
                        text-ink
                        ${
                            feature.featured
                                ? "text-lg"
                                : "text-base"
                        }
                    `}
                >
                    {title}
                </h3>

                {meta ? (
                    <p
                        className="
                            mt-2
                            text-sm
                            text-muted
                        "
                    >
                        {meta}
                    </p>
                ) : null}
            </div>
        </article>
    );


    /* ---------------------------------------------------------------------- */
    /* No destination                                                        */
    /* ---------------------------------------------------------------------- */

    if (
        !feature.href
    ) {
        return (
            <div
                className={`
                    overflow-hidden
                    rounded-card
                    border
                    border-line
                    bg-surface
                    shadow-sm
                    ${
                        feature.featured
                            ? "md:col-span-2"
                            : ""
                    }
                `}
            >
                {content}
            </div>
        );
    }


    /* ---------------------------------------------------------------------- */
    /* Internal route                                                        */
    /* ---------------------------------------------------------------------- */

    if (
        feature.href.startsWith(
            "/"
        )
    ) {
        return (
            <Link
                to={
                    feature.href
                }
                className={
                    getHomeEditorialCardClassName(
                        feature.featured
                    )
                }
            >
                {content}
            </Link>
        );
    }


    /* ---------------------------------------------------------------------- */
    /* External URL                                                          */
    /* ---------------------------------------------------------------------- */

    return (
        <a
            href={
                feature.href
            }
            target="_blank"
            rel="noreferrer"
            className={
                getHomeEditorialCardClassName(
                    feature.featured
                )
            }
        >
            {content}
        </a>
    );
}


/* -------------------------------------------------------------------------- */
/* Card style                                                                 */
/* -------------------------------------------------------------------------- */

function getHomeEditorialCardClassName(
    featured:
        boolean
): string {
    return `
        group
        overflow-hidden
        rounded-card
        border
        border-line
        bg-surface
        text-inherit
        no-underline
        shadow-sm
        transition
        hover:-translate-y-0.5
        hover:border-dino-300
        hover:shadow-md
        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-dino-500
        focus-visible:ring-offset-2
        ${
            featured
                ? "md:col-span-2"
                : ""
        }
    `;
}


/* -------------------------------------------------------------------------- */
/* Export                                                                     */
/* -------------------------------------------------------------------------- */

export {
    HomeEditorialCard,
    HomePage
};
